/**
 * Shared UI component for UI Toggle.
 * PARENT-CHILD CONTRACT: parent screens provide the data and configuration that
 * this wrapper renders or normalizes for reuse across the application.
 */
import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';

/**
 * COMPONENT: Ui Toggle
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Documents the Angular declaration that connects this class to templates, dependency injection, or template transforms.
 */
@Component({
  selector: 'ui-toggle',
  standalone: true,
  templateUrl: './ui-toggle.component.html',
  styleUrl: './ui-toggle.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiToggleComponent {
  /** PARENT-CHILD CONTRACT: checked documents the input state boundary used by this file. */
  readonly checked = input(false);
  /** PARENT-CHILD CONTRACT: disabled documents the input state boundary used by this file. */
  readonly disabled = input(false);
  /** PARENT-CHILD CONTRACT: ariaLabel documents the input state boundary used by this file. */
  readonly ariaLabel = input('Toggle');
  readonly toggled = output<boolean>();

  /**
   * BUSINESS LOGIC: onToggle
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onToggle(nextValue: boolean): void {
    if (this.disabled()) {
      return;
    }

    this.toggled.emit(nextValue);
  }
}
